import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StickyNote } from "lucide-react";

function normalizeFishingWay(value = "") {
  const normalized = value.trim().toLowerCase();

  if (normalized === "shore") return "Shore";
  if (normalized === "boat") return "Boat";

  return value;
}

function getFishingWayToken(value = "") {
  const normalized = value.trim().toLowerCase();

  if (normalized === "shore" || normalized === "бряг") return "shore";
  if (normalized === "boat" || normalized === "лодка") return "boat";

  return normalized;
}

function getFishingTypeToken(value = "") {
  const normalized = value.trim().toLowerCase();

  if (normalized === "spinning" || normalized === "спининг") return "spinning";

  return normalized;
}

function listIncludesFishingWay(options = [], value = "") {
  const token = getFishingWayToken(value);

  return options.some((option) => getFishingWayToken(option) === token);
}

function listIncludesFishingType(options = [], value = "") {
  const token = getFishingTypeToken(value);

  return options.some((option) => getFishingTypeToken(option) === token);
}

function resolveFishingTypeKey(regionData, value = "") {
  const token = getFishingTypeToken(value);

  if (regionData?.[token]) {
    return token;
  }

  return Object.keys(regionData || {}).find(
    (key) => getFishingTypeToken(key) === token
  );
}

function normalizeRegionMatchValue(value = "") {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2019\u2018`\u02bb\u02bc]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function GearSuggestions({
  selectedTrip,
  fishingMethod,
  useDefaultMethod = false,
}) {
  const { t, i18n } = useTranslation();
  const [gearData, setGearData] = useState({ active: null, fallback: null });
  const activeLanguage = (i18n.resolvedLanguage || i18n.language || "en")
    .split("-")[0]
    .toLowerCase();

  useEffect(() => {
    let cancelled = false;
    const activePath =
      activeLanguage === "bg"
        ? "/JSON/speciesGearData-BG.json"
        : "/JSON/speciesGearData.json";

    const loadJson = (path) =>
      fetch(path).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load ${path}`);
        }

        return res.json();
      });

    const loadEnglishData = loadJson("/JSON/speciesGearData.json");
    const loadActiveData =
      activeLanguage === "bg" ? loadJson(activePath) : loadEnglishData;

    Promise.all([
      loadActiveData.catch((err) => {
        console.error("Error loading localized gear JSON:", err);
        return null;
      }),
      loadEnglishData.catch((err) => {
        console.error("Error loading fallback gear JSON:", err);
        return null;
      }),
    ]).then(([activeData, fallbackData]) => {
      if (cancelled) return;

      setGearData({
        active: activeData || fallbackData,
        fallback: fallbackData || activeData,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [activeLanguage]);

  if (!selectedTrip || !gearData.active) return null;

  // Normalize species: lowercase, drop parenthetical qualifiers, strip hyphens
  // AND whitespace so "European Sea Bass" ≡ "European seabass".
  const clean = (str) =>
    (str || "")
      .toLowerCase()
      .replace(/\s*\(.*?\)/g, "")
      .replace(/[-\s]/g, "")
      .trim();

  // Explicit aliases for trip/fish species whose normalized form does not
  // match any gear key directly, but whose scientific name proves the gear
  // JSON does contain an entry under a different common name.
  // Keys/values are already in clean()-normalized form.
  const SPECIES_ALIASES = {
    // fish-data.json "Barracuda (European / Mediterranean)" — Sphyraena sphyraena
    // → gear key "European barracuda (Sphyraena sphyraena)"
    barracuda: "europeanbarracuda",
    // fish-data.json "Red Seabream" — Pagrus pagrus
    // → gear key "Red porgy (Pagrus pagrus)"
    redseabream: "redporgy",
  };

  const tripSpecies = clean(selectedTrip.species);
  const aliasedTripSpecies = SPECIES_ALIASES[tripSpecies] || tripSpecies;
  const findSpeciesKey = (data) => {
    const keys = Object.keys(data || {});
    return (
      keys.find((key) => clean(key) === tripSpecies) ||
      keys.find((key) => clean(key) === aliasedTripSpecies)
    );
  };

  const activeSpeciesKey = findSpeciesKey(gearData.active);
  const fallbackSpeciesKey = findSpeciesKey(gearData.fallback);
  const speciesKey = activeSpeciesKey || fallbackSpeciesKey;

  if (!speciesKey) return null;

  const displaySpeciesBlock =
    gearData.active?.[activeSpeciesKey] || gearData.fallback?.[fallbackSpeciesKey];
  const referenceSpeciesBlock =
    gearData.fallback?.[fallbackSpeciesKey] || displaySpeciesBlock;

  if (!displaySpeciesBlock || !referenceSpeciesBlock) return null;

  // Find Region based on trip location
  const location = normalizeRegionMatchValue(selectedTrip.location || "");
  let locationKey = location;

  // Dubai mapping → Arabian Gulf
  if (location.includes("dubai")) {
    locationKey = "arabian gulf";
  }

  let matchedRegion = null;

  const referenceRegionNames = Object.keys(referenceSpeciesBlock.regions || {});
  const displayRegionNames = Object.keys(displaySpeciesBlock.regions || {});

  for (const regionName of referenceRegionNames) {
    if (locationKey.includes(normalizeRegionMatchValue(regionName))) {
      matchedRegion = regionName;
      break;
    }
  }

  // fallback: pick first region
  if (!matchedRegion) {
    matchedRegion = referenceRegionNames[0] || displayRegionNames[0];
  }

  const matchedRegionIndex = referenceRegionNames.indexOf(matchedRegion);
  const displayMatchedRegion =
    (matchedRegionIndex >= 0 && displayRegionNames[matchedRegionIndex]) ||
    matchedRegion;
  const referenceRegionData = referenceSpeciesBlock.regions?.[matchedRegion];
  const displayRegionData =
    displaySpeciesBlock.regions?.[displayMatchedRegion] || referenceRegionData;

  if (!referenceRegionData || !displayRegionData) return null;

  const defaultWay =
    normalizeFishingWay(selectedTrip.fishingType) &&
    listIncludesFishingWay(
      referenceRegionData.wayOfFishing,
      normalizeFishingWay(selectedTrip.fishingType)
    )
      ? normalizeFishingWay(selectedTrip.fishingType)
      : referenceRegionData.wayOfFishing?.[0] || "";
  const defaultType =
    referenceRegionData.fishingType?.find((option) =>
      resolveFishingTypeKey(referenceRegionData, option)
    ) ||
    referenceRegionData.fishingType?.[0] ||
    "";

  const way = fishingMethod?.way || (useDefaultMethod ? defaultWay : "");
  const type = fishingMethod?.type || (useDefaultMethod ? defaultType : "");

  if (!way || !type) return null;

  // Check WAY + TYPE allowed
  if (!listIncludesFishingWay(referenceRegionData.wayOfFishing, way)) return null;
  if (!listIncludesFishingType(referenceRegionData.fishingType, type)) return null;

  const typeKey = resolveFishingTypeKey(referenceRegionData, type);
  const typeBlock =
    displayRegionData[typeKey] || referenceRegionData[typeKey];
  if (!typeBlock) return null;

  const suggestion = {
    weight: displayRegionData.typicalWeightKg,
    lureLength: typeBlock.lureLengthCm,
    lureWeight: typeBlock.lureWeightG,
    specialLures: typeBlock.specialLures,
    notes: typeBlock.notes,
    teethRisk: typeBlock.teethRisk,
    abrasionRisk: typeBlock.abrasionRisk,
    greediness: typeBlock.greediness,
    region: displayMatchedRegion,
    reelSize: typeBlock.reelSizeRange,
    recommendedReels: typeBlock.recommendedReels,
  };
  const rodAction =
    Array.isArray(suggestion.lureWeight) && suggestion.lureWeight.length >= 2
      ? `${t("gear_rod_action")}: ${suggestion.lureWeight[0]}-${suggestion.lureWeight[1]} g`
      : "";
  const reelSizeText =
    Array.isArray(suggestion.reelSize) && suggestion.reelSize.length >= 2
      ? `${suggestion.reelSize[0]}-${suggestion.reelSize[1]}`
      : suggestion.reelSize
        ? String(suggestion.reelSize)
        : "";
  const weightText =
    Array.isArray(suggestion.weight) && suggestion.weight.length >= 2
      ? `${suggestion.weight[0]}-${suggestion.weight[1]} kg`
      : "";
  const reelDetails = [
    reelSizeText,
    ...(Array.isArray(suggestion.recommendedReels)
      ? suggestion.recommendedReels
      : []),
  ].filter(Boolean);
  const lureDetails = Array.isArray(suggestion.specialLures)
    ? suggestion.specialLures
    : [];

  return (
    <div
      className={`mytrips-gear-suggestions ${
        useDefaultMethod ? "mytrips-gear-suggestions--planned" : ""
      }`}
    >
      <section
        className="mytrips-gear-modern"
        aria-label={t("gear_suggestions_mobile_title", {
          defaultValue: "Recommended Gear",
        })}
      >
        <div className="mytrips-gear-modern__head">
          <span className="mytrips-gear-modern__head-icon" aria-hidden="true">
            <img
              src="/icons/gear/gear-set.png"
              alt=""
              className="mytrips-gear-modern__custom-icon"
            />
          </span>
          <h3>
            {t("gear_suggestions_mobile_title", {
              defaultValue: "Recommended Gear",
            })}
          </h3>
        </div>

        <div className="mytrips-gear-modern__meta">
          <span>
            {t("gear_region")}: {suggestion.region}
          </span>
          {weightText && (
            <span>
              {t("gear_typical_size")}: {weightText}
            </span>
          )}
        </div>

        <div className="mytrips-gear-modern__rows">
          <div className="mytrips-gear-modern__row">
            <span className="mytrips-gear-modern__icon" aria-hidden="true">
              <img
                src="/icons/gear/rod.png"
                alt=""
                className="mytrips-gear-modern__custom-icon"
              />
            </span>
            <div className="mytrips-gear-modern__copy">
              <h4>{t("gear_rod_mobile_label", { defaultValue: "Rod" })}</h4>
              {rodAction && <p>{rodAction}</p>}
              <p>{t("gear_rod_length")}</p>
            </div>
          </div>

          <div className="mytrips-gear-modern__row">
            <span className="mytrips-gear-modern__icon" aria-hidden="true">
              <img
                src="/icons/gear/reel.png"
                alt=""
                className="mytrips-gear-modern__custom-icon"
              />
            </span>
            <div className="mytrips-gear-modern__copy">
              <h4>
                {t("gear_reel_mobile_label", {
                  defaultValue: "Reel",
                })}
              </h4>
              {reelDetails.map((item, i) => (
                <p key={`reel-${i}`}>{item}</p>
              ))}
            </div>
          </div>

          <div className="mytrips-gear-modern__row">
            <span className="mytrips-gear-modern__icon" aria-hidden="true">
              <img
                src="/icons/gear/lure.png"
                alt=""
                className="mytrips-gear-modern__custom-icon"
              />
            </span>
            <div className="mytrips-gear-modern__copy">
              <h4>{t("gear_best_lures")}</h4>
              <ul>
                {lureDetails.map((lure, i) => (
                  <li key={`lure-${i}`}>{lure}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mytrips-gear-modern__row">
            <span className="mytrips-gear-modern__icon" aria-hidden="true">
              <StickyNote size={19} strokeWidth={2.2} />
            </span>
            <div className="mytrips-gear-modern__copy">
              <h4>{t("gear_notes")}</h4>
              <p>{suggestion.notes}</p>
            </div>
          </div>
        </div>
      </section>

      <div
        className="mytrips-gear-legacy"
        style={{
          marginTop: "25px",
          padding: "15px",
          border: "1px solid #cfeff4",
          borderRadius: "10px",
          background: "#f4fcff",
        }}
      >
      <h3 style={{ color: "#007ea7" }}>
        🎣 {t("gear_suggestions_title", { species: speciesKey })}
      </h3>

      <p>
        <strong>{t("gear_region")}:</strong> {suggestion.region}
      </p>

      <p>
        <strong>{t("gear_typical_size")}:</strong>{" "}
        {suggestion.weight[0]}–{suggestion.weight[1]} kg
      </p>

      <p>
  <strong>{t("gear_rod_spinning")}:</strong>
  <br />
  • {t("gear_rod_action")}: {suggestion.lureWeight[0]}–
  {suggestion.lureWeight[1]} g
  <br />
  • {t("gear_rod_length")}
</p>


      <p>
        <strong>{t("gear_recommended_reels")}:</strong>
      </p>
      <ul>
        {suggestion.recommendedReels?.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>

      <p>
        <strong>{t("gear_best_lures")}:</strong>
      </p>
      <ul>
        {suggestion.specialLures.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>

      <p>
        <strong>{t("gear_notes")}:</strong> {suggestion.notes}
      </p>
    </div>
    </div>
  );
}
